// src/pages/guia/GuiaComisiones.js
//
// Histórico de comisiones del Guía por factura pagada. Filtros año/mes.

import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Card, CardContent, Stack, Box, Alert,
  LinearProgress, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, Grid,
} from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { guia as api } from '../../api/guiaApi';
import { useApp } from '../../context/AppContext';

const fmtCOP = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);

export default function GuiaComisiones() {
  const { notificar } = useApp();
  const hoy = new Date();
  const [filtros, setFiltros] = useState({ anio: '', mes: '' });
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    const q = {};
    if (filtros.anio) q.anio = filtros.anio;
    if (filtros.mes)  q.mes  = filtros.mes;
    api.comisiones(q)
      .then(r => setData(r))
      .catch(e => notificar(e.mensajeUsuario || 'Error.', 'error'))
      .finally(() => setCargando(false));
  }, [filtros.anio, filtros.mes, notificar]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <RequestQuoteIcon color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h4" fontFamily="Fraunces">Mis comisiones</Typography>
          <Typography variant="body2" color="text.secondary">
            Cada mensualidad pagada por un afiliado genera una comisión.
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={3}>
              <TextField select fullWidth size="small" label="Año" value={filtros.anio}
                         onChange={e => setFiltros(f => ({ ...f, anio: e.target.value }))}>
                <MenuItem value="">Todos</MenuItem>
                {[2024, 2025, 2026, 2027].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField select fullWidth size="small" label="Mes" value={filtros.mes}
                         onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))}>
                <MenuItem value="">Todos</MenuItem>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m =>
                  <MenuItem key={m} value={m}>{String(m).padStart(2, '0')}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
              {data && (
                <>
                  <Typography variant="caption" color="text.secondary">Total período</Typography>
                  <Typography variant="h5" color="success.main" fontWeight={700}>
                    {fmtCOP(data.totalComision)}
                  </Typography>
                  <Typography variant="caption">
                    {data.totalRegistros} registros al {data.porcentajeComision}%
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {cargando && <LinearProgress />}
      {data && data.comisiones.length === 0 && (
        <Alert severity="info">Sin comisiones en este período.</Alert>
      )}
      {data && data.comisiones.length > 0 && (
        <Card>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Factura</TableCell>
                <TableCell>Negocio</TableCell>
                <TableCell>Producto</TableCell>
                <TableCell>Período</TableCell>
                <TableCell>Pagada</TableCell>
                <TableCell align="right">Base gravable</TableCell>
                <TableCell align="right">%</TableCell>
                <TableCell align="right">Tu comisión</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.comisiones.map(c => (
                <TableRow key={c.facturaId} hover>
                  <TableCell><Typography variant="caption">{c.numeroFactura}</Typography></TableCell>
                  <TableCell>{c.nombreComercial || c.empresa}</TableCell>
                  <TableCell>{c.producto}</TableCell>
                  <TableCell>{String(c.periodoMes).padStart(2, '0')}/{c.periodoAnio}</TableCell>
                  <TableCell>{c.fechaPago?.substring(0, 10)}</TableCell>
                  <TableCell align="right">{fmtCOP(c.baseGravable)}</TableCell>
                  <TableCell align="right">{c.porcentajeComision}%</TableCell>
                  <TableCell align="right"><b>{fmtCOP(c.comision)}</b></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </Container>
  );
}
